import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'

import { defineListener, errorView, resourceScopeCleared, userScopeCleared, type ModuleListener } from '@krp/client-runtime'

import { classifyCommandError } from '../../../shared/model/commandOutcome'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import {
  photoMainRequested,
  photoOperationCancelRequested,
  photoOperationResendRequested,
  photosReloadRequested,
  photoUploadRequested,
} from './commands'
import { photoOperationKey } from './keys'
import {
  abortPhotoSend,
  beginPhotoSend,
  dropAllPhotoFiles,
  dropPhotoFile,
  dropPhotoFilesOf,
  endPhotoSend,
  photoFile,
  replacePhotoFile,
} from './photoFiles'
import { PHOTO_OUTCOME_VIEW } from './outcomes'
import { photoOperationProgress, photoOperationResent, photoOperationSettled, photoOperationStarted } from './photosSlice'
import type { PhotoPortOutcome, PhotosPorts, PhotosPortsFactory } from './ports'
import type { MainPhotoOperation, PhotoAction, PhotoOperation, PhotosIntentState, UploadPhotoOperation } from './types'

/** Не чаще раза в это число мс обновляем кольцо: события передачи идут десятки раз в секунду. */
const PROGRESS_THROTTLE_MS = 120

/** Человек отменил отправку: запрос прерван, байты на месте — можно отправить ещё раз тем же ключом. */
const CANCELLED_TEXT = 'Отправка отменена. Снимок остался на экране: его можно отправить ещё раз'

async function run(operation: PhotoOperation, ports: PhotosPorts, dispatch: Dispatch<UnknownAction>) {
  let outcome: PhotoPortOutcome
  if (operation.action === 'upload') {
    const token = operation.fileToken
    const file = token === null ? undefined : photoFile(token)
    if (token === null || file === undefined) {
      dispatch(
        photoOperationSettled({
          key: operation.key,
          operationId: operation.operationId,
          status: 'failed',
          resendable: false,
          error: { kind: 'client', code: 'file-lost', message: 'Снимок больше не в памяти страницы — выберите файл заново', correlationId: null },
          outcome: null,
        }),
      )
      return
    }
    const signal = beginPhotoSend(token)
    dispatch(photoOperationProgress({ key: operation.key, operationId: operation.operationId, phase: 'sending', loaded: 0, total: null }))
    let lastDispatchAt = 0
    try {
      outcome = await ports.upload(operation, file, {
        ...(signal !== undefined ? { signal } : {}),
        onProgress: (loaded, total) => {
          const now = Date.now()
          const finished = total !== null && loaded >= total
          // Последнее событие («всё ушло») не теряется: по нему кольцо переходит к «Сохраняем в 1С».
          if (!finished && now - lastDispatchAt < PROGRESS_THROTTLE_MS) return
          lastDispatchAt = now
          dispatch(
            photoOperationProgress({
              key: operation.key,
              operationId: operation.operationId,
              phase: finished ? 'saving' : 'sending',
              loaded,
              total,
            }),
          )
        },
      })
    } finally {
      endPhotoSend(token)
    }
  } else {
    outcome = await ports.setMain(operation)
  }
  if (outcome.ok) {
    const done = PHOTO_OUTCOME_VIEW[outcome.outcome].done
    dispatch(
      photoOperationSettled({
        key: operation.key,
        operationId: operation.operationId,
        status: done ? 'succeeded' : 'failed',
        resendable: false,
        error: null,
        outcome: outcome.outcome,
      }),
    )
    if (operation.fileToken !== null) dropPhotoFile(operation.fileToken)
    ports.reload(operation.resourceId, operation.refKey)
    return
  }
  const view = errorView(outcome.error)
  if (view.code === 'aborted') {
    // Отмена человеком: определённый исход (на сервере ничего не завершено), снимок остаётся для повтора.
    dispatch(
      photoOperationSettled({
        key: operation.key,
        operationId: operation.operationId,
        status: 'failed',
        resendable: true,
        error: { ...view, code: 'cancelled', message: CANCELLED_TEXT },
        outcome: null,
      }),
    )
    return
  }
  // 2xx, но тело не разобрано: запись, скорее всего, выполнена. Это «исход неизвестен» (проверка тем же ключом), а не «не выполнено».
  const failure =
    view.kind === 'invalid-response' && operation.action === 'upload'
      ? ({ status: 'unknown', resendable: true, error: view } as const)
      : classifyCommandError(outcome.error)
  dispatch(
    photoOperationSettled({
      key: operation.key,
      operationId: operation.operationId,
      status: failure.status,
      resendable: failure.resendable,
      error: failure.error,
      outcome: null,
    }),
  )
  // Байты нужны только пока команду можно отправить ещё раз тем же ключом.
  if (!failure.resendable && operation.fileToken !== null) dropPhotoFile(operation.fileToken)
  if (failure.status === 'unknown' || failure.status === 'conflict') ports.reload(operation.resourceId, operation.refKey)
}

/** Общие поля новой операции; остальное (ключ файла или байты) добавляет вид операции. */
function newOperationFields(
  ports: PhotosPorts,
  base: { resourceId: string; refKey: string; expectedConfigurationVersion: string },
  action: PhotoAction,
) {
  const operationId = ports.newOperationId()
  return {
    key: photoOperationKey(base.resourceId, base.refKey, action),
    resourceId: normalizeRid(base.resourceId),
    refKey: base.refKey,
    expectedConfigurationVersion: base.expectedConfigurationVersion,
    operationId,
    idempotencyKey: `onec-ui:photo-${action}:${operationId}`,
    status: 'sending',
    phase: null,
    loaded: 0,
    total: null,
    resendable: false,
    error: null,
    outcome: null,
    startedAt: ports.now(),
  } as const
}

const isBusy = (operation: PhotoOperation | undefined) =>
  operation !== undefined && (operation.status === 'sending' || operation.status === 'unknown')

export function createPhotosListeners<S>(
  makePorts: PhotosPortsFactory,
  intent: (state: S) => PhotosIntentState,
): ModuleListener<S>[] {
  return [
    defineListener<S, ReturnType<typeof photoMainRequested>>(photoMainRequested.match, async (action, api) => {
      const { resourceId, refKey, fileKey, expectedConfigurationVersion } = action.payload
      if (isBusy(intent(api.getModuleState()).operations[photoOperationKey(resourceId, refKey, 'main')])) return
      const ports = makePorts(api.dispatch)
      const operation: MainPhotoOperation = {
        ...newOperationFields(ports, { resourceId, refKey, expectedConfigurationVersion }, 'main'),
        action: 'main',
        fileKey,
        fileToken: null,
        fileName: null,
      }
      api.dispatch(photoOperationStarted(operation))
      await run(operation, ports, api.dispatch)
    }),

    defineListener<S, ReturnType<typeof photoUploadRequested>>(photoUploadRequested.match, async (action, api) => {
      const { resourceId, refKey, fileToken, fileName, expectedConfigurationVersion } = action.payload
      const previous = intent(api.getModuleState()).operations[photoOperationKey(resourceId, refKey, 'upload')]
      if (isBusy(previous)) {
        dropPhotoFile(fileToken)
        return
      }
      if (previous?.fileToken != null) dropPhotoFile(previous.fileToken)
      const ports = makePorts(api.dispatch)
      const operation: UploadPhotoOperation = {
        ...newOperationFields(ports, { resourceId, refKey, expectedConfigurationVersion }, 'upload'),
        action: 'upload',
        fileKey: null,
        fileToken,
        fileName,
      }
      api.dispatch(photoOperationStarted({ ...operation, phase: ports.prepare === undefined ? 'sending' : 'preparing' }))
      if (ports.prepare !== undefined) {
        const source = photoFile(fileToken)
        if (source !== undefined) {
          // Сжатие и вырезание метаданных — на устройстве, до отправки; повтор и «Проверить исход» идут уже сжатыми байтами.
          const prepared = await ports.prepare(source, fileName)
          if (!prepared.ok) {
            api.dispatch(
              photoOperationSettled({
                key: operation.key,
                operationId: operation.operationId,
                status: 'failed',
                resendable: false,
                error: prepared.error,
                outcome: null,
              }),
            )
            dropPhotoFile(fileToken)
            return
          }
          replacePhotoFile(fileToken, prepared.file)
        }
      }
      await run(operation, ports, api.dispatch)
    }),

    defineListener<S, ReturnType<typeof photoOperationCancelRequested>>(photoOperationCancelRequested.match, (action, api) => {
      const operation = intent(api.getModuleState()).operations[action.payload.key]
      // Только пока тело уходит: после «всё отправлено» служба уже работает, и прерывать нельзя — исход стал бы неизвестен.
      if (operation === undefined || operation.action !== 'upload' || operation.status !== 'sending' || operation.phase !== 'sending') return
      if (operation.fileToken !== null) abortPhotoSend(operation.fileToken)
    }),

    defineListener<S, ReturnType<typeof photoOperationResendRequested>>(
      photoOperationResendRequested.match,
      async (action, api) => {
        const operation = intent(api.getModuleState()).operations[action.payload.key]
        if (operation === undefined || operation.status === 'sending' || !operation.resendable) return
        api.dispatch(photoOperationResent({ key: operation.key }))
        await run(operation, makePorts(api.dispatch), api.dispatch)
      },
    ),

    defineListener<S, ReturnType<typeof photosReloadRequested>>(photosReloadRequested.match, (action, api) => {
      makePorts(api.dispatch).reload(action.payload.resourceId, action.payload.refKey)
    }),

    // Выход: снимки прежнего человека не остаются в памяти страницы.
    defineListener<S, ReturnType<typeof userScopeCleared>>(userScopeCleared.match, () => {
      dropAllPhotoFiles()
    }),
    defineListener<S, ReturnType<typeof resourceScopeCleared>>(resourceScopeCleared.match, (action) => {
      dropPhotoFilesOf(action.payload.resourceId)
    }),
  ]
}
